<template>
    <div class="form">
        <form action="">
            <h2 class="profile__title">Photos</h2>
            <div class="profile__media">
                <div class="drop" v-if="artist.photos && artist.photos.length === 0">
                    <strong><i class="fa-regular fa-image"></i> Gallery</strong>
                    <span v-if="isPermission">Click to upload files </span>
                    <a
                        v-if="isPermission"
                        @click="$refs.imageInput.click()"
                        href="javascript:void(0)"
                        class="btn btn_primary-outline pl-5 pr-5 mt-3">Select Files</a>
                </div>
                <div v-else>
                    <span class="form-label">Gallery</span>
                        <draggable v-model="localArtistPhotos" @end="onDragEnd()" class="files">
                            <div
                                class="files__item"
                                v-for="(photo, index) in localArtistPhotos"
                                :key="photo.id">
                                <a :href="`/uploads/${photo.file.file_path}`" target="_blank">
                                    <img
                                        :src="`/uploads/${photo.file.file_path}`"
                                        alt="Photo"
                                        width="120"
                                    >
                                </a>
                                <button
                                    v-if="isPermission"
                                    type="button"
                                    class="files__delete"
                                    @click="deleteFile(photo.id, 'photos')"
                                >
                                    <svg width="12" height="12" aria-hidden="true">
                                        <use href="/img/svg/symbol-defs.svg#icon-close-9"></use>
                                    </svg>
                                </button>
                                <span class="files__num">{{index + 1}}</span>
                            </div>
                        </draggable>
                    <div class="add-link mt-3">
                        <a
                            v-if="isPermission"
                            href="javascript:void(0)"
                            @click="$refs.imageInput.click()"
                        >+ Add Photo</a>
                    </div>
                    <span class="form-note">To change the range of the images click and drug.</span>
                </div>
            </div>
            <input
                type="file"
                style="display: none"
                ref="imageInput"
                id="file-upload"
                accept="image/*"
                multiple
                class="icon"
                @change="selectFiles('imageInput')"
            >



            <h2 class="profile__title">Videos</h2>
            <div class="profile__media">
                <div class="drop" v-if="artist.videos && artist.videos.length === 0">
                    <strong><i class="fa-solid fa-video"></i> Gallery</strong>
                    <span v-if="isPermission">Click to upload files</span>
                    <a
                        v-if="isPermission"
                        href="javascript:void(0)"
                        class="btn btn_primary-outline pl-5 pr-5 mt-3"
                        @click="$refs.videoInput.click()"
                    >Select Files</a>
                </div>


                <div v-else >
                    <span class="form-label">Gallery</span>
                    <div class="files files_video">
                        <div class="files__item"
                             v-for="(video, index) in artist.videos"
                             :key="video.id"
                        >
                            <button
                                v-if="isPermission"
                                type="button"
                                class="files__delete"
                                @click="deleteFile(video.id, 'videos')"
                            >
                                <svg width="12" height="12" aria-hidden="true">
                                    <use href="/img/svg/symbol-defs.svg#icon-close-9"></use>
                                </svg>
                            </button>
                            <video
                                width="120"
                                :id="`video-${video.id}`"
                            >
                               <source :src="`/uploads${video.file.file_path}`">
                            </video>

                            <span
                                v-if="!isPlaying[video.id]"
                                class="files__play"
                                @click="showCustomModal('videoModal', video.id)"
                            >
                                <i class="fa-solid fa-circle-play"></i>
                            </span>
                            <span class="files__num">{{ index + 1}}</span>

                            <VideoModal
                                :modal-id="getCustomModalId('videoModal', video.id)"
                                :video="`/uploads${video.file.file_path}`"
                            />
                        </div>
                    </div>
                    <div class="add-link mt-3">
                        <a
                            v-if="isPermission"
                            href="javascript:void(0)"
                            @click="$refs.videoInput.click()"
                        >+ Add Video</a>
                    </div>
                </div>
            </div>

            <VideoLinks :saved-links="artist.video_links" v-if="isPermission"/>

            <input
                type="file"
                style="display: none"
                ref="videoInput"
                id="file-upload"
                accept="video/mp4,video/avi,video/mkv"
                multiple
                class="icon"
                @change="selectFiles('videoInput')"
            >

        </form>
    </div>
</template>

<script>
import {mapState, mapActions} from "pinia";
import {useArtistStore} from "../../stores/ArtistStore";
import ArtistService from "../../services/ArtistService";
import ModalHandler from "../../mixins/ModalHandler";
import VideoModal from "../Common/VideoModal.vue";
import ActionModal from "../Common/ActionModal.vue";
import draggable from "vuedraggable";
import VideoLinks from "../Common/VideoLinks.vue";

export default {
    name: 'ArtistGallery',
    components: {VideoLinks, ActionModal, VideoModal, draggable},
    mixins: [ModalHandler],
    props: {
        adminType: String
    },
    data() {
        return {
            files: [],
            isPlaying: {},
            localArtistPhotos: []
        }
    },
    computed: {
        ...mapState(useArtistStore, {
            artist: 'artist',
            sortedPhotos: 'sortedPhotos',
            adminPermissions: 'adminPermissions'
        }),
        isPermission() {
            return this.adminType !== 'agent'
        },
    },
    watch: {
        sortedPhotos: {
            handler(newValue) {
                this.localArtistPhotos = [...newValue];
            },
            immediate: true,
            deep: true,
        },
        'artist.photos': {
            handler(newPhotos) {
                const store = useArtistStore();
                store.updateArtistPhotos(newPhotos);
            },
            deep: true,
        },
    },

    methods: {
        ...mapActions(useArtistStore, {
            updatePhotoOrder: 'updatePhotoOrder'
        }),
        onDragEnd() {
            const newOrderPhotos = this.localArtistPhotos.map((item) => item.id);
            this.updatePhotoOrder(newOrderPhotos)

            this.sendPositionToBackend(newOrderPhotos);
        },

        async sendPositionToBackend(newOrderPhotos) {
            this.$showLoadingMessage()
            try {
                await ArtistService.updatePhotosOrder(newOrderPhotos)
                this.$showSuccessMessage('The order was updated')
            } catch (e) {
                this.$showErrorMessage('The order was not updated')
            }
        },
        async deleteFile(id, type) {

            try {
                this.artist[type] = this.artist[type].filter(video => video.id !== id);
                const uploadMethod = type === 'photos'
                    ? await ArtistService.deletePhotoFromGallery
                    : await ArtistService.deleteVideoFromGallery;

                const { data: response } = await uploadMethod(id);
                this.$showSuccessMessage('The file was deleted successfully')

            } catch (e) {
                this.$showErrorMessage('The file was not deleted. Please try again')
            }

        },
        async selectFiles(type) {
            const uploadedFiles = this.$refs[type].files;

            const validFiles = await this.validateAndPrepareFiles(uploadedFiles);

            if (validFiles.length === 0) {
                return;
            }

            this.files = [
                ...new Map([...this.files, ...validFiles].map(item => [item.name, item])).values()
            ];

            if (type === 'imageInput') {
                await this.sendPhoto(validFiles)
            } else {
               await  this.sendVideo(validFiles)
            }

        },

        async validateAndPrepareFiles(uploadedFiles) {
            const MAX_FILE_SIZE = 150 * 1024 * 1024;
            const files = await Promise.all(
                Array.from(uploadedFiles).map(async (file) => {
                    if (file.size > MAX_FILE_SIZE) {
                        this.$showErrorMessage(`The file ${file.name} exceeds the maximum size of 150 MB.`);
                    } else {
                        try {
                            const content = await this.readFileAsync(file);
                            return {
                                file,
                                name: file.name,
                                content,
                            };
                        } catch (error) {
                            console.error("Помилка при читанні файлу:", error);
                            return null;
                        }
                    }
                })
            );
            return files.filter(Boolean);
        },

        async readFileAsync(file) {
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result);
                reader.onerror = reject;
                reader.readAsDataURL(file);
            });
        },

        async sendVideo(files) {
            this.$showLoadingMessage()
            const formData = new FormData();
            for (let i = 0; i < files.length; i++) {
                formData.append('videos[]', files[i].file);
            }
            try {
                const { data: response } = await ArtistService.uploadVideoInGallery(this.artist.id, formData);
                this.$showSuccessMessage('The video/s was uploaded')
                this.artist.videos = response
                this.files = []
            } catch (error) {
                this.$showErrorMessage();
            }
        },

        async sendPhoto(files) {
            this.$showLoadingMessage()
            const formData = new FormData();
            for (let i = 0; i < files.length; i++) {
                formData.append('photos[]', files[i].file);
            }
            try {
                const { data: response } = await ArtistService.uploadPhotoInGallery(this.artist.id, formData);
                this.artist.photos = response
                this.$showSuccessMessage('The photo/s was uploaded')
                this.files = []
            } catch (error) {
                this.$showErrorMessage();
            }

        }
    },
    mounted() {
        this.localArtistPhotos = [...this.sortedPhotos];
    }

}

</script>



<style scoped>

</style>
